'use client';

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { navigateHrmPage } from '@/lib/hrm-routing';
import { useHrmNavigation } from '@/hooks/use-hrm-navigation';
import { usePermissions } from '@/hooks/use-permissions';
import { getOrgSetupReadiness } from '@/services/org-setup.service';

/** Soft gate banner when workspace is not fully go-live. */
export default function OrgSetupReadinessBanner() {
  const { has, isAdmin } = usePermissions();
  const { currentPage } = useHrmNavigation();
  const canRead = isAdmin || has('platform.settings.read');

  const { data } = useQuery({
    queryKey: ['org-setup-readiness'],
    queryFn: getOrgSetupReadiness,
    enabled: canRead,
    staleTime: 60_000,
    retry: 1,
  });

  if (!canRead || !data) return null;
  if (data.goLiveState === 'live') return null;
  if (currentPage === 'setup') return null;

  const incomplete = data.steps.filter((s) => s.required && !s.complete);

  return (
    <div
      className="mx-4 mt-4 lg:mx-6 rounded-xl border border-teal-300/80 dark:border-teal-800 bg-teal-50 dark:bg-teal-950/40 px-4 py-3 text-sm text-teal-950 dark:text-teal-50"
      role="status"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold">
            Workspace setup {data.percentComplete}% complete
            {data.goLiveState === 'soft_live' ? ' — ready to go live' : ''}
          </p>
          <p className="mt-1 text-xs leading-relaxed opacity-90">
            {incomplete.length > 0
              ? `Still needed: ${incomplete.map((s) => s.label).join(', ')}. Leave applications, self-onboard invites, and check-in stay blocked until those steps exist.`
              : 'All required steps are done. Open setup and mark go-live when you are ready.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigateHrmPage('setup')}
          className="shrink-0 rounded-lg bg-teal-800 px-3 py-1.5 text-xs font-medium text-white dark:bg-teal-600"
        >
          Open setup
        </button>
      </div>
    </div>
  );
}
